import styled from '@emotion/styled';
import React, { useCallback, useRef } from 'react';
import { round } from '@hotjar/webapp/features/Playbacks/Timeline/Section/SectionList';
import { useResizeObserver } from '@hotjar/webapp/hooks/useResizeObserver';
import { useRealTimeStore } from '@hotjar/webapp/features/Playbacks/store/realTimeStore';
import type { EventMarker } from '../types';
import { TIMELINE_PADDING } from '../Timeline/constants';
import { Underline } from './Underline';
import { isRecordingFromProductLink } from '../utils/isRecordingFromProductLink';

interface Props {
  sidePanelEventMarkers?: EventMarker[];
  recordingDuration: number;
  firstFilterMatch: EventMarker | null;
}
const UnderlineWrapper = styled.div`
  width: 100%;
  position: absolute;
  top: 25px;
  height: 4px;
  padding: 0 ${TIMELINE_PADDING}px 0px ${TIMELINE_PADDING}px;
  > div {
    top: 2px;
    width: 100%;
    position: relative;
    height: 4px;
  }
`;

export const UnderlineList = React.memo(
  ({ sidePanelEventMarkers, recordingDuration, firstFilterMatch }: Props): JSX.Element | null => {
    const barRef = useRef<HTMLDivElement>(null);
    const { width } = useResizeObserver(barRef);
    const isHovering = useRealTimeStore((state) => state.isHovering);

    const hoveredPageIndex = useRealTimeStore((state) => state.hoveredPageIndex);
    const currentPageHovered = useRealTimeStore(
      useCallback(
        (state) => {
          const [firstSectionHovered, ...rest] = state.sectionInfoList.filter(
            (section) => section.pageIndex === hoveredPageIndex,
          );
          if (!firstSectionHovered) {
            return null;
          }
          const totalDurationMs = rest.reduce((acc, curr) => {
            return curr.durationInMs + acc;
          }, firstSectionHovered.durationInMs);

          const res = {
            startTimeInMs: firstSectionHovered.startTimeInMs,
            endTimeInMs: firstSectionHovered.startTimeInMs + totalDurationMs,
            durationInMs: totalDurationMs,
          };
          return res;
        },
        [hoveredPageIndex],
      ),
    );
    if (sidePanelEventMarkers?.length === 0) {
      return null;
    }
    const jumpstartMarkers = sidePanelEventMarkers?.filter((marker) => marker.is_highlighted);
    const showFilterMatchTooltip = !isRecordingFromProductLink();

    if (jumpstartMarkers?.length === 0 || currentPageHovered === null) {
      return null;
    }

    return (
      <UnderlineWrapper ref={barRef}>
        <div>
          {jumpstartMarkers?.map(({ time, duration }) => {
            const xPositionInPx = round((time * width) / recordingDuration);
            const xWidthInPx = duration && round((duration * width) / recordingDuration);
            return duration ? (
              <Underline
                xPositionInPx={xPositionInPx}
                xWidthInPx={xWidthInPx}
                key={time + duration}
                showFilterMatchTooltip={showFilterMatchTooltip}
                isRespectivePageHovered={
                  isHovering &&
                  time >= currentPageHovered.startTimeInMs &&
                  time + duration <= currentPageHovered.endTimeInMs
                }
                showOnCreate={!!firstFilterMatch && firstFilterMatch.time === time}
              />
            ) : null;
          })}
        </div>
      </UnderlineWrapper>
    );
  },
);
